import React, { useState, useEffect, useCallback } from 'react';
import InsufficientCreditsModal from './InsufficientCreditsModal';
import { auth } from '../firebase/firebase';
import { saveOrShareImage, isIOSDevice } from '../utils/screenshot';
import { useT } from '../i18n';
import './DownloadModal.css';

// The image-to-image endpoint is a plain HTTPS function, not a callable — see
// functions/generateImageFlux.js.
const FN_BASE =
  `https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net`;
const FLUX_ENDPOINT = `${FN_BASE}/generateImageWithFlux`;

// The canvas is uploaded at its OWN aspect ratio — no cropping, padding or
// stretching, since the model accepts arbitrary input dimensions. Only two
// practical limits apply, both handled by scaling alone:
//   • keep the payload well under the upload cap
//   • avoid absurd aspect ratios that waste pixels on empty space
const MAX_UPLOAD_SIDE = 1536;   // keeps a PNG comfortably under the size cap
const MAX_ASPECT      = 2.5;

// Capture the WebGL canvas as PNG bytes at its own aspect ratio. Only downscales
// (never up), so a small viewport is sent untouched.
function canvasToUploadBlob(canvas) {
  const sw = canvas.width, sh = canvas.height;
  const ar = sw / sh;

  // An ultra-wide viewport past the API's 2.5:1 limit is the ONE case that
  // needs a crop — there's no way to represent it otherwise. Trim the long
  // edge symmetrically to exactly 2.5:1 rather than distorting the image.
  let cx = 0, cy = 0, cw = sw, ch = sh;
  if (ar > MAX_ASPECT)      { cw = sh * MAX_ASPECT; cx = (sw - cw) / 2; }
  else if (ar < 1 / MAX_ASPECT) { ch = sw * MAX_ASPECT; cy = (sh - ch) / 2; }

  // Scale down to keep the payload small, preserving aspect exactly.
  const scale = Math.min(1, MAX_UPLOAD_SIDE / Math.max(cw, ch));
  const tw = Math.max(64, Math.round(cw * scale));
  const th = Math.max(64, Math.round(ch * scale));

  const out = document.createElement('canvas');
  out.width = tw; out.height = th;
  out.getContext('2d').drawImage(canvas, cx, cy, cw, ch, 0, 0, tw, th);
  return new Promise((resolve) => out.toBlob(resolve, 'image/png'));
}

// The request goes as JSON, so the PNG has to travel as a data URL.
function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload  = () => resolve(String(fr.result));
    fr.onerror = () => reject(new Error('read failed'));
    fr.readAsDataURL(blob);
  });
}

// Sent with every request, overriding the function's own fallback.
//
// MUST STAY SCENE-AGNOSTIC. An earlier version named the specific contents of
// one demo room ("the sofa, the table, the chairs, the two 'Tunify' logos on
// the walls") on the theory that an explicit inventory beats a generic
// "preserve the layout". It does — but only for that one room. This prompt goes
// out for EVERY room, so in a bedroom the model was being handed a list of
// furniture that isn't there and duly invented it, logos included.
//
// COLOUR IS THE OTHER HALF. Left to itself the model "improves" materials by
// warming them: a grey tile floor came back terracotta, because the prompt had
// asked for clear wood grain and said nothing about keeping colours. Every
// colour instruction below is load-bearing — the model changes what it is not
// explicitly told to preserve.
//
// LIGHTING IS PER-MOOD. The scene renders at a chosen time of day (morning /
// evening / night), but a single static prompt described "soft natural
// lighting" regardless — which reads as daylight, so an evening or night room
// came back relit as midday. The clause below is chosen from the SAME
// timeOfDay the 3D view is using, so the render keeps the mood it was captured
// in, including where the light comes from and how dark the shadows go.
const LIGHTING_BY_MOOD = {
  morning: [
    'The scene is lit by cool, bright morning daylight coming through the window.',
    'Keep the same overall brightness: bright, airy and evenly lit, with crisp',
    'directional sunlight, soft light-grey shadows and gentle ambient occlusion.',
  ].join(' '),
  evening: [
    'The scene is lit by warm, low-angle golden-hour sunlight coming through the',
    'window, plus warm interior lamps.',
    'Keep the same overall brightness and mood: warm amber cast, long soft',
    'shadows stretching away from the window, deeper contrast than daytime, and',
    'a dim warm glow in the corners. Do NOT relight this as daytime.',
  ].join(' '),
  night: [
    'It is NIGHT. The only light comes from the interior lamps and ceiling',
    'lights; outside the window is dark.',
    'Keep the scene genuinely dark: pools of warm artificial light around each',
    'lamp falling off quickly into deep shadow, dark cool blue-black corners,',
    'strong contrast, and visible light spill on nearby surfaces.',
    'Do NOT brighten the room, and do NOT relight it as daytime or sunset.',
  ].join(' '),
};

// ─── STRONGLY-COLOURED WALLS ────────────────────────────────────────────────
//
// One sentence, added only for walls the model demonstrably cannot hold: a
// custom colour the user mixed themselves, or one of the few very vivid presets.
// A chartreuse wall came back washed to pale sage — those hues are rare in
// interior photography, so the model normalises them toward something commoner.
//
// SCOPE IS THE WHOLE POINT. Everything else — neutrals, ordinary colours,
// textures, patterns — is left completely alone, because those already come back
// correctly and earlier attempts to describe them made them worse. The caller
// decides what qualifies (see ThreeDRoom); nothing here widens that.
//
// NO COLOUR NAMES. Naming a colour in a prompt is an instruction to produce it,
// and naming shades is what previously turned a uniformly yellow room's wall
// white and pushed pale yellow to orange. The hex is exact and carries no such
// pull, and the input image is right there.
function vividWallClause(surfaces) {
  const walls = (surfaces?.walls || []).filter(
    (h) => typeof h === 'string' && /^#?[0-9a-f]{6}$/i.test(h.trim())
  );
  if (!walls.length) return '';

  const list = walls.map((h) => h.toUpperCase()).join(' and ');
  return `The wall paint colour is exactly ${list}. Reproduce that hue and`
    + ' saturation exactly — do not wash it out, mute it, lighten it or shift it'
    + ' toward any other colour.';
}

// Sent with every request, overriding the function's own fallback.
// `mood` is the current time of day so the lighting clause matches the capture.
function buildArchPrompt(mood, surfaces) {
  return [
    'Turn this 3D interior render into a photorealistic photograph of the same room.',
    // Colour is stated first and hardest — it is the instruction the model is
    // most prone to quietly ignore.
    'CRITICAL: reproduce every colour exactly 1:1 with the input image.',
    // Empty for every wall except a custom or very vivid one — see above.
    vividWallClause(surfaces),
    'The floor colour and pattern, the wall colours, the ceiling colour, and the',
    'colour, material and finish of every piece of furniture must be identical',
    'to the input — same hue, same saturation, same tone.',
    'Do not warm, cool, tint, re-tone, brighten or darken any surface, and do not',
    'substitute any material for a different one.',
    'Keep the identical camera angle, perspective, proportions and layout, and the',
    'exact position of every object, window and door.',
    LIGHTING_BY_MOOD[mood] || LIGHTING_BY_MOOD.morning,
    // Stated after the lighting clause so the two can't be read as conflicting:
    // light and shadow may become photographic, surface colour may not shift.
    'Apply that lighting WITHOUT changing the base colour of any surface — only',
    'shading, shadow and reflection may change, never the material colour itself.',
    'Only add photographic realism: fine surface texture, physically accurate',
    'material response, realistic shadows and ambient occlusion.',
    'Do not add, remove, replace or move anything.',
    'Do not add any logo, text, sign, watermark, poster or artwork.',
    // filter: the wall clause is empty in the ordinary case, and joining an
    // empty string would leave a double space in the prompt.
  ].filter(Boolean).join(' ');
}

// ─── DOWNLOAD MODAL ──────────────────────────────────────────────────────────
// Sits between the "Download" button and the actual save. Two ways out:
//
//   • Download original — hands the already-captured PNG straight to the app's
//     existing save path (saveOrShareImage), which is what the button used to
//     do on its own.
//   • Generate FHD AI  — sends that same PNG to the generateImageWithFlux
//     callable and downloads what comes back.
//
// The OpenAI key is never involved on this side: the browser only ever talks to
// the Cloud Function, which resolves the key from Secret Manager in its own
// runtime. Nothing secret is imported, bundled, or reachable from devtools here.
//
// Both `capture` and `canvasRef` are FUNCTIONS, resolved at click time rather
// than when the modal opens — a WebGL drawing buffer can be cleared between
// frames, so anything grabbed early may be blank by the time it's used.
//   capture()   → watermarked PNG data URL, for the plain download
//   canvasRef() → the live <canvas>, for the AI upload (raw bytes, no base64)
export default function DownloadModal({ open, onClose, capture, canvasRef, filename = 'tunify-render.png', onNotify, timeOfDay = 'morning', surfaces = null }) {
  const { t } = useT();
  // Holds the MODEL KEY of the render in flight ('openai' | 'flux'), or false.
  // A key rather than a boolean so only the card that was pressed shows a
  // spinner while both are disabled.
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError]         = useState(null);
  // Set only from the server reply, so the figures shown are the ones that
  // actually refused the request. null = no dialog.
  const [noCredits, setNoCredits] = useState(null);
  // Finished render held for a SECOND tap. iOS only permits the Share Sheet
  // inside a live user gesture, and generation takes ~30-60s — by the time it
  // resolves the original click is long gone, so an automatic save is silently
  // refused there. Parking the result and letting the user tap "Save" gives us
  // a fresh gesture. Desktop/Android save immediately and never see this.
  const [resultUrl, setResultUrl] = useState(null);

  // Reset transient state whenever the modal is reopened, so a previous
  // error/spinner/result never greets the next open.
  useEffect(() => {
    if (open) { setIsLoading(false); setError(null); setResultUrl(null); }
  }, [open]);

  // Esc to dismiss — but never mid-generation, or the user loses a render
  // they've already paid for (in quota terms) with no way to get it back.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !isLoading) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, isLoading, onClose]);

  // Save a URL (data:, blob:, or remote https:) to the user's device.
  // A remote URL is fetched into a blob first: cross-origin responses ignore
  // the `download` attribute and would otherwise just navigate the tab away.
  const downloadUrl = useCallback(async (url, name) => {
    let objectUrl = null;
    let href = url;
    if (!url.startsWith('data:')) {
      const res = await fetch(url);
      if (!res.ok) throw new Error('fetch failed');
      const blob = await res.blob();
      objectUrl = URL.createObjectURL(blob);
      href = objectUrl;
    }
    const a = document.createElement('a');
    a.href = href;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Revoke on the next tick — revoking synchronously can cancel the download
    // in some browsers before it has actually started reading the blob.
    if (objectUrl) setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
  }, []);

  const handleOriginal = useCallback(() => {
    const dataUrl = capture?.();
    if (!dataUrl) { setError(t('dl_capture_failed')); return; }
    // Existing save path — handles the iOS Share Sheet fallback.
    onNotify?.({ type: 'save', dataUrl, filename });
    onClose();
  }, [capture, filename, onClose, onNotify, t]);

  // IMAGE-TO-IMAGE. The canvas goes up as a PNG data URL alongside the prompt.
  // Sending the IMAGE, not just the prompt, is what makes the backend use
  // flux-kontext-pro rather than plain text-to-image — that is what keeps this
  // exact camera angle, layout and furniture, leaving only the materials and
  // lighting to become photographic.
  const handleGenerate = useCallback(async () => {
    setError(null);

    // Awaited: the 3D view re-renders at full resolution before handing the
    // canvas over, so the AI never works from a downscaled image just because
    // the display quality is set low (see captureForAI in ThreeDRoom). A plain
    // canvas is returned unchanged by await, so this stays correct either way.
    const canvas = await canvasRef?.();
    if (!canvas) { setError(t('dl_capture_failed')); return; }

    setIsLoading('flux');
    try {
      // Raw PNG bytes at the canvas's own aspect ratio. toBlob (not toDataURL)
      // so the image travels as binary rather than base64. The WebGL buffer is
      // read here, inside the click, while it's still valid.
      const blob = await canvasToUploadBlob(canvas);
      if (!blob) throw new Error(t('dl_capture_failed'));

      const user = auth.currentUser;
      if (!user) throw new Error(t('dl_ai_signin'));
      const idToken = await user.getIdToken();

      // Replicate's SDK takes the image as a data URI, so this goes as JSON.
      // Sending the image (rather than the prompt alone) is what makes the
      // backend use Kontext and keep the layout — see generateImageFlux.js.
      const inputImage = await blobToDataUrl(blob);
      const res = await fetch(FLUX_ENDPOINT, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ prompt: buildArchPrompt(timeOfDay, surfaces), inputImage }),
      });
      const payload = await res.json().catch(() => ({}));
      // ── NOT AN ERROR, A PRICE ───────────────────────────────────────────
      // The server refused this for want of credits: no provider was called,
      // nothing failed, and there is a specific thing the reader can do. It
      // must not reach the generic red banner, which would say the AI broke.
      //
      // A plain return rather than a throw, so the finally below still resets
      // the button — the reader can act on the dialog and try again.
      if (!res.ok && payload.code === 'INSUFFICIENT_CREDITS') {
        setNoCredits({
          current: payload.currentCredits,
          required: payload.requiredCredits,
        });
        return;
      }
      if (!res.ok) throw new Error(payload.error || t('dl_ai_failed'));

      // Flux returns `imageUrl`, plus base64 fields when the function managed to
      // inline the image — prefer those, since a data URL cannot expire or be
      // blocked by CORS.
      const outUrl = payload.dataUrl
        || (payload.imageBase64
          && `data:${payload.mimeType || 'image/png'};base64,${payload.imageBase64}`)
        || payload.imageUrl;
      if (!outUrl) throw new Error(t('dl_ai_failed'));
      const outName = filename.replace(/\.png$/i, '-realistic.png');

      if (isIOSDevice()) {
        // Hold it for a second tap — see the note on `resultUrl`.
        setResultUrl({ url: outUrl, name: outName });
        return;
      }
      await downloadUrl(outUrl, outName);
      onClose();
      onNotify?.({ type: 'success', message: t('dl_ai_success') });
    } catch (err) {
      setError(err?.message || t('dl_ai_failed'));
    } finally {
      setIsLoading(false);
    }
  }, [canvasRef, filename, downloadUrl, onClose, onNotify, t]);

  // Runs inside the tap, so iOS accepts the Share Sheet (save to Photos), with
  // saveOrShareImage's new-tab long-press fallback behind it.
  const handleSaveResult = useCallback(() => {
    if (!resultUrl) return;
    saveOrShareImage(resultUrl.url, resultUrl.name, t('dl_ai'));
    onClose();
    onNotify?.({ type: 'success', message: t('dl_ai_success') });
  }, [resultUrl, onClose, onNotify, t]);

  if (!open) return null;

  return (
    <div
      className="dlm-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="dlm-title"
      // Clicking the backdrop dismisses, except while generating.
      onClick={() => { if (!isLoading) onClose(); }}
    >
      <div className="dlm-panel" onClick={e => e.stopPropagation()}>
        <div className="dlm-head">
          <span id="dlm-title" className="dlm-title">{t('dl_title')}</span>
          <button
            className="dlm-close"
            onClick={onClose}
            disabled={isLoading}
            aria-label={t('cancel')}
          >✕</button>
        </div>

        <p className="dlm-sub">{resultUrl ? t('dl_ready_sub') : t('dl_sub')}</p>

        {/* Finished render awaiting a tap (iOS only — see `resultUrl`). Showing
            the image also confirms what is about to be saved. */}
        {resultUrl ? (
          <div className="dlm-result">
            <img className="dlm-result-img" src={resultUrl.url} alt={t('dl_ai')} />
            <button className="dlm-card dlm-card--ai" onClick={handleSaveResult}>
              <span className="dlm-card-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path d="M12 3v12m0 0 4-4m-4 4-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </span>
              <span className="dlm-card-body">
                <span className="dlm-card-title">{t('dl_save')}</span>
                <span className="dlm-card-desc">{t('dl_save_desc')}</span>
              </span>
            </button>
          </div>
        ) : (
        <div className="dlm-options">
          {/* Original */}
          <button className="dlm-card" onClick={handleOriginal} disabled={isLoading}>
            <span className="dlm-card-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M12 3v12m0 0 4-4m-4 4-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </span>
            <span className="dlm-card-body">
              <span className="dlm-card-title">{t('dl_original')}</span>
              <span className="dlm-card-desc">{t('dl_original_desc')}</span>
            </span>
          </button>

          {/* One card per AI model — each calls its own function directly.
              `isLoading` holds the model key, so the card being used spins while
              the others disable.
              `locked` blocks a model without deleting it: the card still shows
              (so users know it's coming) but can't be pressed, and the whole
              request path stays wired. Flip the flag to re-enable — nothing
              else needs changing. */}
          {[
            { key: 'flux',   title: t('dl_model_flux'),   desc: t('dl_model_flux_desc')   },
          ].map(m => {
            const busy = isLoading === m.key;
            return (
              <button
                key={m.key}
                className={`dlm-card dlm-card--ai${busy ? ' dlm-card--busy' : ''}${m.locked ? ' dlm-card--locked' : ''}`}
                onClick={() => { if (!m.locked) handleGenerate(m.key); }}
                disabled={!!isLoading || m.locked}
                aria-disabled={m.locked || undefined}
              >
                <span className="dlm-card-icon">
                  {busy ? (
                    <span className="dlm-spinner" />
                  ) : (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <path d="M12 3l1.9 4.6L18.5 9.5 13.9 11.4 12 16l-1.9-4.6L5.5 9.5l4.6-1.9L12 3Z" fill="currentColor"/>
                      <path d="M18 15l.9 2.1L21 18l-2.1.9L18 21l-.9-2.1L15 18l2.1-.9L18 15Z" fill="currentColor" opacity="0.7"/>
                    </svg>
                  )}
                </span>
                <span className="dlm-card-body">
                  <span className="dlm-card-title">
                    {busy ? t('dl_ai_loading') : m.title}
                  </span>
                  <span className="dlm-card-desc">
                    {m.locked ? t('dl_model_locked_desc') : busy ? t('dl_ai_loading_desc') : m.desc}
                  </span>
                </span>
                {!busy && (
                  <span className="dlm-badge">
                    {m.locked ? `🔒 ${t('dl_model_soon')}` : 'FHD'}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        )}

        {error && <div className="dlm-error" role="alert">⊗ {error}</div>}
      </div>
      {/* Position here is cosmetic: the dialog portals to <body>, so it is
          never clipped by this panel wherever it sits in the tree. */}
      <InsufficientCreditsModal
        open={Boolean(noCredits)}
        currentCredits={noCredits?.current}
        requiredCredits={noCredits?.required}
        onClose={() => setNoCredits(null)}
      />
    </div>
  );
}
